import styled, { createGlobalStyle } from "styled-components";

const GlobalStyles = createGlobalStyle`
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700;900&display=swap');

:root {
  /* Typography Scale */
  --font-size-xs: 12px;
  --font-size-sm: 14px;
  --font-size-base: 16px;
  --font-size-lg: 18px;
  --font-size-xl: 20px;
  --font-size-2xl: 24px;
  --font-size-3xl: 32px;
  --font-size-4xl: 48px;
  --font-size-5xl: 64px;
  
  /* Line Heights */
  --line-height-tight: 1.25;
  --line-height-normal: 1.5;
  --line-height-relaxed: 1.75;
  
  /* Font Weights */
  --font-weight-light: 300;
  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;
  --font-weight-extrabold: 800;
  --font-weight-black: 900;
  
  /* Primary Colors - brand yellow */
  --primary-50: #fef9e7;
  --primary-100: #fef0c7;
  --primary-400: #fcd13f;
  --primary-500: #f5c842;
  --primary-600: #e6b83a;
  
  /* Neutral Colors */
  --gray-50: #fafafa;
  --gray-100: #f5f5f5;
  --gray-200: #e5e5e5;
  --gray-300: #d4d4d4;
  --gray-400: #a3a3a3;
  --gray-500: #737373;
  --gray-600: #525252;
  --gray-700: #404040;
  --gray-800: #262626;
  --gray-900: #171717;
  
  /* Brand Colors */
  --brand-dark: #402204;
  --brand-dark-lighter: #5c3208;
  
  /* Spacing System */
  --space-1: 4px;
  --space-2: 8px;
  --space-3: 12px;
  --space-4: 16px;
  --space-5: 20px;
  --space-6: 24px;
  --space-8: 32px;
  --space-10: 40px;
  --space-12: 48px;
  --space-16: 64px;
  --space-20: 80px;
  --space-24: 96px;

  /* Surfaces */
  --bg-base: #ffffff;
  --bg-elevated: #f8f7f4;
  --card: #ffffff;
  --card-hover: #fbfaf5;
  --border-subtle: rgba(31, 26, 18, 0.12);
  --border-strong: rgba(31, 26, 18, 0.22);

  /* Text */
  --text-primary: #1f1a12;
  --text-secondary: #5c5346;
  /* 흰 바탕에서 4.6:1 — 작은 글자도 AA를 넘긴다. */
  --text-muted: #77706a;

  /* Accent — 글자로 쓸 때는 어두운 황토, 면으로 쓸 때는 브랜드 노랑 */
  --accent: #b58200;
  --accent-strong: #f5c842;
  --accent-soft: rgba(252, 209, 63, 0.18);
  --accent-border: rgba(224, 165, 0, 0.45);

  /* Elevation (홈에서는 쓰지 않는다) */
  --shadow-card: 0 1px 2px rgba(64, 34, 4, 0.05),
    0 12px 28px rgba(64, 34, 4, 0.06);
  --shadow-card-hover: 0 4px 10px rgba(64, 34, 4, 0.08),
    0 18px 40px rgba(64, 34, 4, 0.1);

  /* Layout */
  --max-width: 1120px;
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
}

* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    font-family: 'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

html {
  scroll-behavior: smooth;
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

body {
  background: var(--bg-base);
  color: var(--text-primary);
  font-feature-settings: 'kern' 1, 'liga' 1, 'calt' 1;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
}
*::-webkit-scrollbar {
  width: 0.5px;
}
*::-webkit-scrollbar-track {
  background: transparent;
}
*::-webkit-scrollbar-thumb {
  background: rgba(155, 155, 155, 0.5);
  border-radius: 20px;
  border: transparent;
}
`;

export const Container = styled.div`
  z-index: 1;
  width: 100%;
  max-width: 1300px;
  margin-right: auto;
  margin-left: auto;
  padding-right: 50px;
  padding-left: 50px;
  @media screen and (max-width: 750px) {
    padding-right: 30px;
    padding-left: 30px;
  }
`;

export const Button = styled.button`
  border-radius: var(--radius-sm);
  background: ${({ primary }) => (primary ? "var(--primary-400)" : "var(--text-primary)")};
  white-space: nowrap;
  padding: ${({ big }) => (big ? "var(--space-4) var(--space-12)" : "var(--space-3) var(--space-6)")};
  color: ${({ primary }) => (primary ? "var(--brand-dark)" : "#ffffff")};
  font-size: ${({ fontBig }) => (fontBig ? "var(--font-size-lg)" : "var(--font-size-base)")};
  font-weight: var(--font-weight-semibold);
  outline: none;
  border: none;
  cursor: pointer;
  transition: background 0.18s ease;
  letter-spacing: -0.01em;
  
  &:hover {
    background: ${({ primary }) => (primary ? "var(--primary-500)" : "#3a3125")};
  }
  
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }
  
  @media screen and (max-width: 750px) {
    width: 100%;
    font-size: var(--font-size-base);
  }
`;

/* ───────────── 홈 화면 공용 레이아웃 ─────────────
 * 흰 바탕, 왼쪽 정렬, 얇은 구분선, 여백으로만 위계를 만든다.
 * 카드·그림자·그라데이션은 쓰지 않는다.
 */

export const Section = styled.section`
  position: relative;
  padding: var(--space-20) 0;
  background: var(--bg-base);
  border-top: ${({ $rule }) => ($rule ? "1px solid var(--border-subtle)" : "0")};

  @media screen and (max-width: 768px) {
    padding: var(--space-12) 0;
  }
`;

export const SectionInner = styled.div`
  position: relative;
  width: 100%;
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 0 var(--space-6);
`;

export const SectionHead = styled.div`
  max-width: 44rem;
  margin-bottom: var(--space-12);

  @media screen and (max-width: 768px) {
    margin-bottom: var(--space-8);
  }
`;

export const SectionHeading = styled.h2`
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: var(--font-weight-extrabold);
  line-height: 1.25;
  letter-spacing: -0.03em;
  color: var(--text-primary);
  word-break: keep-all;
  text-wrap: pretty;
`;

/* 게임 키아트의 노란 형광펜 강조. 색 글자 대신 이걸 쓴다. */
export const Mark = styled.mark`
  background: var(--primary-400);
  color: inherit;
  padding: 0 0.12em;
  margin: 0 -0.04em;
  border-radius: 0.12em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

export const SectionSubheading = styled.p`
  margin-top: var(--space-3);
  max-width: 36rem;
  font-size: 1.0625rem;
  line-height: 1.7;
  color: var(--text-secondary);
  word-break: keep-all;
  text-wrap: pretty;

  @media screen and (max-width: 768px) {
    font-size: var(--font-size-base);
  }
`;

export const InkButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.8125rem 1.5rem;
  border-radius: var(--radius-sm);
  background: var(--text-primary);
  color: #ffffff;
  font-size: 0.9375rem;
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.18s ease;

  &:hover {
    background: #3a3125;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }
`;

/* 게임 키아트의 굵은 외곽선·스티커 느낌을 가져온 버튼 */
export const StickerButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.875rem 1.625rem;
  border: 2px solid var(--text-primary);
  border-radius: var(--radius-md);
  background: var(--text-primary);
  color: #ffffff;
  font-size: 1rem;
  font-weight: var(--font-weight-bold);
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 4px 4px 0 var(--brand-dark);
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0 var(--brand-dark);
  }

  &:active {
    transform: translate(2px, 2px);
    box-shadow: 2px 2px 0 var(--brand-dark);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
  }
`;

export const OutlineButton = styled(StickerButton)`
  background: transparent;
  color: var(--text-primary);
  box-shadow: none;

  &:hover {
    transform: none;
    box-shadow: none;
    background: rgba(31, 26, 18, 0.08);
  }

  &:active {
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline-color: var(--text-primary);
  }
`;

export const TextLink = styled.a`
  display: inline-block;
  color: var(--text-primary);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-medium);
  text-decoration: underline;
  text-decoration-color: var(--border-strong);
  text-underline-offset: 0.3em;
  transition: text-decoration-color 0.18s ease;

  &:hover {
    text-decoration-color: var(--text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
    border-radius: 2px;
  }
`;

export default GlobalStyles;
